/**
 * /en/admin/structure/paragraphs_type/vuc_terms_and_conditions_modal/fields
 */
import CLASS_NAMES from "@js/constants/css-constants.es6.js";
import { DOM_EVENTS } from "@js/constants/html-constants.es6.js";
import { KEYS } from "@js/constants/js-constants.es6.js";
import Element from "@js/core/element.es6.js";
import Field from "@js/core/field.es6.js";
import PrimaryButtonField from "@js/fields/primary-button-field.es6.js";
import Tab from "@js/helpers/tab.es6.js";
import ModalParagraph from "@js/paragraphs/modal-paragraph.es6.js";
import CheckboxFormElementParagraphField from "@js/fields/form-element-checkbox-paragraph-field.es6.js";

export default class VirtualUrgentCareTermsAndConditionsModalParagraph extends ModalParagraph {
  constructor({ overlayService, parentElement }) {
    super({
      overlayService,
      machineName: 'vuc_terms_and_conditions_modal',
      parentElement,
    });
  }

  get buttonAcceptAndContinue() {
    return new PrimaryButtonField({
      machineName: 'field_primary_button',
      parentElement: this.activeModal,
    });
  }

  get buttonCancelAppointment() {
    return new PrimaryButtonField({
      machineName: 'field_secondary_button',
      parentElement: this.activeModal,
    });
  }

  get inputAcceptTermsAndConditions() {
    return new CheckboxFormElementParagraphField({
      machineName: 'field_form_element_accept_terms',
      parentElement: this.activeModal,
    });
  }

  get inputAcceptFinancialResponsibility() {
    return new CheckboxFormElementParagraphField({
      machineName: 'field_form_element_accept_financ',
      parentElement: this.activeModal,
    });
  }

  get tabFinancialResponsibility() {
    return new Field({
      machineName: 'field_text_button_2',
      parentElement: this.activeModal,
    });
  }

  get tabTermsAndConditions() {
    return new Field({
      machineName: 'field_text_button',
      parentElement: this.activeModal,
    });
  }

  get tabs() {
    return new Element({
      selector:
        '.paragraph--type--vuc-terms-and-conditions-modal > .tab-wrapper',
      parentElement: this.activeModal,
    });
  }

  get sectionFinancialResponsibility() {
    return new Element({
      selector: '.financial-responsibility-section',
      parentElement: this.activeModal,
    });
  }

  get sectionTermsAndConditions() {
    return new Element({
      selector: '.terms-and-conditions-section',
      parentElement: this.activeModal,
    });
  }

  initialize() {
    this.#updateTabs();
    this.#updateInputAcceptTermsAndConditions();
    this.#updateInputAcceptFinancialResponsibility();
    this.#updateCancelAppointmentButton();
  }

  open(acceptHandler) {
    super.open();

    this.inputAcceptTermsAndConditions.paragraph.uncheck();
    this.inputAcceptFinancialResponsibility.paragraph.uncheck();

    this.#updateAcceptAndContinueButton(acceptHandler);
  }

  #updateTabs() {
    const $termsAndConditionsTab = this.tabTermsAndConditions.$(0);
    const $financialResponsibilityTab = this.tabFinancialResponsibility.$(0);

    new Tab({
      $tabElement: $termsAndConditionsTab,
      selected: true,
      selectionHandler: () => this.#onTermsAndConditionsTabSelected(true),
    });

    new Tab({
      $tabElement: $financialResponsibilityTab,
      selected: false,
      selectionHandler: () => this.#onFinancialResponsibilityTabSelected(false),
    });
  }

  #onTermsAndConditionsTabSelected() {
    this.sectionFinancialResponsibility.$().addClass(CLASS_NAMES.HIDDEN);
    this.sectionTermsAndConditions.$().removeClass(CLASS_NAMES.HIDDEN);
  }

  #onFinancialResponsibilityTabSelected() {
    this.sectionTermsAndConditions.$().addClass(CLASS_NAMES.HIDDEN);
    this.sectionFinancialResponsibility.$().removeClass(CLASS_NAMES.HIDDEN);
  }

  #updateInputAcceptTermsAndConditions() {
    this.inputAcceptTermsAndConditions.paragraph.toggleRequirement(true);

    this.inputAcceptTermsAndConditions
      .$()
      .change(() => this.#toggleAcceptButton());
  }

  #updateInputAcceptFinancialResponsibility() {
    this.inputAcceptFinancialResponsibility.paragraph.toggleRequirement(true);

    this.inputAcceptFinancialResponsibility
      .$()
      .change(() => this.#toggleAcceptButton());
  }

  #toggleAcceptButton() {
    const acceptTermsAndConditionsChecked = this.inputAcceptTermsAndConditions
      .paragraph.hasValue;
    const acceptFinancialResponsibilityChecked = this
      .inputAcceptFinancialResponsibility.paragraph.hasValue;

    if (
      acceptTermsAndConditionsChecked &&
      acceptFinancialResponsibilityChecked
    ) {
      this.buttonAcceptAndContinue.enable();
    } else {
      this.buttonAcceptAndContinue.disable();
    }
  }

  #updateCancelAppointmentButton() {
    const $buttonCancelAppointment = this.buttonCancelAppointment.$();

    $buttonCancelAppointment.on(DOM_EVENTS.CLICK, event => {
      event.preventDefault();
      this.close();
    });

    $buttonCancelAppointment.on(DOM_EVENTS.KEYPRESS, event => {
      if (event.key === KEYS.ENTER) {
        event.preventDefault();
        this.close();
      }
    });
  }

  #updateAcceptAndContinueButton(callback) {
    const acceptHandler = async () => {
      this.buttonAcceptAndContinue.toggleLoadingState(true);

      try {
        await callback();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.buttonAcceptAndContinue.toggleLoadingState(false);
      }
    };

    const $buttonAcceptAndContinue = this.buttonAcceptAndContinue.$();
    $buttonAcceptAndContinue.off();

    $buttonAcceptAndContinue.on(DOM_EVENTS.CLICK, async (event) => {
      event.preventDefault();
      await acceptHandler();
    });

    $buttonAcceptAndContinue.on(DOM_EVENTS.KEYPRESS, async (event) => {
      if (event.key === KEYS.ENTER) {
        event.preventDefault();
        await acceptHandler();
      }
    });
  }
}
